<template>
    <Transition name="fade">
        <div v-if="isLoading" class="page-loader">
            <div class="spinner"></div>
        </div>
    </Transition>
</template>

<script setup lang="ts">
    import { watch, onBeforeUnmount } from 'vue'

    const props = defineProps<{
        isLoading: boolean
    }>()

    // Shared across all PageLoader instances so simultaneous loaders
    // (e.g. App.vue's router-ready loader and a view's data loader)
    // don't clobber each other's scroll lock. Uses the classic
    // position:fixed body-lock technique, which is more reliable than
    // overflow:hidden alone (works regardless of which element the
    // browser treats as the scrolling box).
    function applyLock() {
        const scrollY = window.scrollY;
        document.body.dataset.scrollLockY = String(scrollY);
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
    }

    function releaseLock() {
        const scrollY = parseInt(document.body.dataset.scrollLockY || '0', 10);
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        delete document.body.dataset.scrollLockY;
        window.scrollTo(0, scrollY);
    }

    function setLocked(locked: boolean) {
        const w = window as any;
        const count = Math.max(0, (w.__pageLoaderLockCount ?? 0) + (locked ? 1 : -1));
        w.__pageLoaderLockCount = count;

        if (count > 0 && !w.__pageLoaderLocked) {
            w.__pageLoaderLocked = true;
            applyLock();
        } else if (count === 0 && w.__pageLoaderLocked) {
            w.__pageLoaderLocked = false;
            releaseLock();
        }
    }

    let isCurrentlyLocked = false
    watch(
        () => props.isLoading,
        (isLoading) => {
            if (isLoading === isCurrentlyLocked) return
            isCurrentlyLocked = isLoading
            setLocked(isLoading)
        },
        { immediate: true }
    )

    onBeforeUnmount(() => {
        if (isCurrentlyLocked) {
            isCurrentlyLocked = false
            setLocked(false)
        }
    })
</script>

<style scoped>
    .page-loader {
        position: fixed;
        top: var(--nav-height, 120px);
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f4f4f4;
    }

    .spinner {
        width: 48px;
        height: 48px;
        border: 4px solid rgba(0, 0, 0, 0.1);
        border-top-color: var(--tenant-primary-color, #333);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.3s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }
</style>
